<template>
  <div ref="container" class="ra-hotspots__wrapper">
    <picture class="ra-hotspots__picture">
      <source media="(min-width: 768px)" :srcset="image.md" />
      <img :src="imageSrc" :alt="imageAlt" @load="handleImageLoad" @error="handleImageLoadError" />
    </picture>
    <ul v-if="isImageFirstLoadResolved && pois.length" class="ra-hotspots__pois">
      <refapp-hotspots-poi
        v-for="(poi, index) in currentScreenPois"
        :key="index"
        :poi="poi"
        :current-poi-position-key="currentPoiPositionKey"
        :is-active="activePoiId === poi.id"
        :container-ref="$refs.container"
        :container-size="containerSize"
        :image-natural-size="imageNaturalSize"
        :screen-size-key="screenSizeKey"
        @openTooltip="openTooltip(poi.id)"
        @closeTooltip="closeTooltip(poi.id, { returnFocusToMarker: $event.returnFocusToMarker })"
      />
    </ul>
  </div>
</template>

<script>
import EventsTracking from '../../../shared/eventsTracking.js';
import { debounce } from '../../../shared/utils/debounce';
import { SCREEN_SIZE } from '../constants';
import RefappHotspotsPoi from './refapp-hotspots-poi.vue';

export default {
  name: 'refapp-hotspots',
  components: {
    RefappHotspotsPoi,
  },
  inject: ['image', 'imageAlt', 'pois', 'taggingId'],
  data() {
    return {
      activePoiId: null,
      containerSize: { width: 0, height: 0 },
      imageNaturalSize: { width: 0, height: 0 },
      isImageFirstLoadResolved: false,
      resizeObserver: null,
      windowWidth: null,
    };
  },
  computed: {
    screenSizeKey() {
      if (this.windowWidth > 767) {
        return SCREEN_SIZE.lg;
      }
      return SCREEN_SIZE.sm;
    },
    imageSrc() {
      return this.image.sm || this.image.md;
    },
    currentPoiPositionKey() {
      if (!this.image.sm) {
        return SCREEN_SIZE.lg;
      }
      return this.screenSizeKey;
    },
    currentScreenPois() {
      return this.pois.filter((poi) => {
        const { x, y } = poi.position[this.currentPoiPositionKey] || {};

        return !!x && !!y;
      });
    },
  },
  watch: {
    screenSizeKey() {
      this.closeTooltip(this.activePoiId);
    },
  },
  created() {
    this.handleWindowResize();
    window.addEventListener('resize', this.handleWindowResize);
  },
  mounted() {
    this.updateContainerSize();
    this.initResizeObserver();
    document.addEventListener('keydown', this.handleKeydown);

    if (this.taggingId) {
      // eslint-disable-next-line sonarjs/constructor-for-side-effects
      new EventsTracking(this.taggingId);
    }
  },
  beforeDestroy() {
    this.beforeUnmount();
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleWindowResize);
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
    },
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.closeTooltip(this.activePoiId);
      }
    },
    handleImageLoad(event) {
      const img = event.target;
      this.imageNaturalSize = {
        width: img.naturalWidth,
        height: img.naturalHeight,
      };
      this.isImageFirstLoadResolved = true;
      this.updateContainerSize();
    },
    handleImageLoadError() {
      this.isImageFirstLoadResolved = true;
    },
    initResizeObserver() {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      if (this.$refs.container) {
        this.resizeObserver.observe(this.$refs.container);
      }
    },
    handleResize: debounce(function () {
      this.updateContainerSize();
    }, 100),
    updateContainerSize() {
      if (!this.$refs.container) return;

      const { width, height } = this.$refs.container.getBoundingClientRect();
      this.containerSize = { width, height };
    },
    openTooltip(poiId) {
      if (this.activePoiId !== poiId) {
        this.activePoiId = poiId;
      }
    },
    closeTooltip(poiId, { returnFocusToMarker = true } = {}) {
      if (!this.activePoiId || poiId !== this.activePoiId) return;

      this.activePoiId = null;

      if (returnFocusToMarker) {
        this.returnFocusToMarker(poiId);
      }
    },
    returnFocusToMarker(poiId) {
      setTimeout(() => {
        if (this.activePoiId) return;
        const marker = this.$refs.container?.querySelector(
          `.js-ra-hotspots-poi-marker[data-poi-id="${poiId}"]`,
        );
        marker?.focus({ preventScroll: true });
      });
    },
  },
};
</script>

<style lang="scss">
@use '../styles/style';
</style>
